import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShieldAlert, Heart, User, ClipboardList, Sparkles, Mountain, CheckCircle2 } from "lucide-react";
import { Waivers } from "@/components/Waivers";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RoomSelection, PaymentPlanPicker, roomTuition } from "@/components/retreat/RoomSelection";

const DEFAULT_CANCELLATION_POLICY =
  "A non-refundable, non-transferable deposit secures your spot. " +
  "Any payments beyond the deposit are refundable if written cancellation is received " +
  "at least 60 days before the retreat's start date. Cancellations made within 60 days " +
  "of the retreat will not be refunded. Aviana reserves the right to cancel any retreat " +
  "at its discretion — in that event, a full refund will be issued.";

const HEALTH_OPTIONS = ["Excellent", "Good", "Fair", "Poor"];
const HAT_SIZES = [
  "XS (21\")", "S (21 5/8\")", "M (22 1/4\")", "L (22 7/8\")",
  "XL (23 1/2\")", "XXL (24\")", "Custom — I'll send measurements",
];
const RIDING_LEVELS = ["None", "Beginner", "Intermediate", "Advanced"];

export default function RegisterRetreat({ slug: slugProp, embedded = false, presetRoom = "", joinId = "" }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const [sp] = useSearchParams();
  const [availability, setAvailability] = useState(null);
  const [invite, setInvite] = useState(null);
  const navigate = useNavigate();
  const [offering, setOffering] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const tierParam = sp.get("tier");
  const initialTier = tierParam === "sisterhood" ? "sisterhood" : tierParam === "trio" ? "trio" : "solo";

  const [form, setForm] = useState({
    pricing_tier: initialTier,
    sisterhood_partner_name: "",
    payment_type: "deposit",
    full_name: "",
    email: "",
    phone: "",
    dob: "",
    address: "",
    emergency_contact_name: "",
    emergency_contact_relationship: "",
    emergency_contact_phone: "",
    health_condition: "",
    health_notes: "",
    injuries_surgeries: "",
    medications: "",
    dietary_restrictions: "",
    hat_size: "",
    horseback_experience: "",
    horseback_experience_notes: "",
    why_this_retreat: "",
    liability_waiver_agreed: false,
    photo_release_agreed: false,
    cancellation_policy_agreed: null,
    room_key: presetRoom,
    extended_stay: false,
    roommate_name: "",
    roommate_email: "",
    invite_roommate: false,
    payment_plan_installments: null,
  });

  useEffect(() => { if (presetRoom) setForm((f) => ({ ...f, room_key: presetRoom })); }, [presetRoom]);

  useEffect(() => {
    if (!offering?.room_options) return;
    api.get(`/retreats/${offering.id}/room-availability`).then(({ data }) => setAvailability(data.available)).catch(() => {});
    if (joinId) {
      api.get(`/retreats/roommate-invite/${joinId}`).then(({ data }) => {
        setInvite(data);
        setForm((f) => ({ ...f, room_key: data.room_key, full_name: f.full_name || data.roommate_name }));
      }).catch(() => toast.error("That invitation link is no longer valid.", { id: "invite-invalid" }));
    }
  }, [offering, joinId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/offerings/${slug}`);
        // Allow retreats OR any offering explicitly configured to use the
        // retreat / day-retreat intake form (e.g., an event day retreat).
        const acceptable = data.type === "retreat"
          || data.registration_form === "destination_retreat"
          || data.registration_form === "day_retreat";
        if (!acceptable) {
          toast.error("This registration form is for retreats only.");
          navigate(`/offering/${slug}`);
          return;
        }
        setOffering(data);
        if (!data.deposit_usd) setForm((f) => ({ ...f, payment_type: "full" }));
        // If user navigated to a tier that's unavailable, fall back to solo.
        if (initialTier === "sisterhood" && !data.sisterhood_price_usd) {
          setForm((f) => ({ ...f, pricing_tier: "solo" }));
        }
        if (initialTier === "trio" && !data.trio_price_usd) {
          setForm((f) => ({ ...f, pricing_tier: "solo" }));
        }
      } catch {
        toast.error("Retreat not found.");
        navigate("/");
      }
    })();
  }, [slug, navigate, initialTier]);

  if (!offering) return <div className="pt-40 container-aviana text-aviana-muted">Loading…</div>;

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const validate = () => {
    const isDayRetreat = offering?.registration_form === "day_retreat";
    const required = ["full_name", "email", "phone", "dob", "address",
      "emergency_contact_name", "emergency_contact_relationship", "emergency_contact_phone",
      "health_condition"];
    if (offering.room_options) {
      const opt = offering.room_options.find((o) => o.key === form.room_key);
      if (!opt) { toast.error("Please choose your room."); document.querySelector('[data-field="room_key"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); return false; }
      if (opt.shared_pair && !invite && !form.roommate_name.trim()) { toast.error("Please tell us who is sharing your suite."); document.querySelector('[data-field="roommate_name"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); return false; }
      if (form.invite_roommate && !invite && !form.roommate_email.trim()) { toast.error("Please add her email so we can send the link."); document.querySelector('[data-field="roommate_email"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); return false; }
      if (invite && !invite.available) { toast.error("This shared-suite spot has already been claimed."); return false; }
    } else if (!isDayRetreat) {
      required.push("hat_size", "horseback_experience");
    }
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error("Please fill in every required field.");
        document.querySelector(`[data-field="${k}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        return false;
      }
    }
    if (form.pricing_tier === "sisterhood" && !form.sisterhood_partner_name.trim()) {
      toast.error("Please tell us who your sister is.");
      document.querySelector('[data-field="sisterhood_partner_name"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    if (form.pricing_tier === "trio" && !form.sisterhood_partner_name.trim()) {
      toast.error("Please share the names of the 2 sisters you're registering with.");
      document.querySelector('[data-field="sisterhood_partner_name"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    if (!form.liability_waiver_agreed) {
      toast.error("Please acknowledge the Waiver & Release of Liability.");
      document.querySelector('[data-field="liability_waiver_agreed"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    if (!form.photo_release_agreed) {
      toast.error("Please acknowledge the Photo Release.");
      document.querySelector('[data-field="photo_release_agreed"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    if (form.cancellation_policy_agreed !== true) {
      toast.error("Please agree to the cancellation policy to continue.");
      document.querySelector('[data-field="cancellation_policy_agreed"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/retreats/registrations", {
        offering_id: offering.id,
        ...form,
        join_reg_id: invite ? joinId : null,
        payment_plan_installments: form.payment_type === "deposit" && offering.room_options ? (form.payment_plan_installments || 1) : null,
      });
      toast.success("Registration saved — let's get your payment sorted.");
      navigate(`/payment/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit registration. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const policy = offering.cancellation_policy || DEFAULT_CANCELLATION_POLICY;
  const isRooms = !!offering.room_options;
  const perPersonPrice = isRooms ? roomTuition(offering, form) : form.pricing_tier === "trio" && offering.trio_price_usd
    ? offering.trio_price_usd
    : form.pricing_tier === "sisterhood" && offering.sisterhood_price_usd
    ? offering.sisterhood_price_usd
    : offering.price_usd;
  const balanceLeft = perPersonPrice - (offering.deposit_usd || 0);
  const balanceTiming = offering.balance_due_label
    ? `by ${offering.balance_due_label}`
    : offering.balance_due_days_before
    ? `${offering.balance_due_days_before} days before the retreat begins`
    : "before the retreat begins";

  return (
    <div className={embedded ? "" : "pt-28 pb-24 bg-aviana-paper"} data-testid="register-retreat-page">
      <div className="container-aviana max-w-3xl">
        {!embedded && (
          <Link to={offering.landing_path || `/offering/${slug}`} className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8">
            <ArrowLeft size={14} /> Back to the retreat
          </Link>
        )}

        <p className="overline-plum mb-3">Retreat Registration</p>
        <h1 className="font-display text-4xl md:text-5xl text-aviana-ink leading-tight">{embedded ? "Join the circle" : offering.title}</h1>
        <p className="mt-3 text-aviana-muted text-[15px]">
          {isRooms
            ? "We can't wait to welcome you. Share a little about yourself below so Holly can prepare your room, your place at the table and your time with the horses with love and care."
            : "Please complete the form below so Holly can prepare a meaningful, safe, and personalised retreat for you — including your cowgirl hat, the horses, and your spot at the table."}
        </p>

        {isRooms && (
          <div className="mt-8">
            <RoomSelection offering={offering} form={form} update={update} availability={availability} invite={invite} />
          </div>
        )}

        {/* Pricing tier toggle (if sisterhood / trio pricing is available) */}
        {!isRooms && (offering.sisterhood_price_usd || offering.trio_price_usd) && (
          <div className="mt-8 bg-white border border-aviana-border rounded-2xl p-6" data-field="pricing_tier" data-testid="tier-toggle">
            <p className="overline-plum mb-3">Pricing</p>
            <div className={`grid gap-3 ${offering.trio_price_usd ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
              <TierCard
                selected={form.pricing_tier === "solo"}
                onClick={() => update({ pricing_tier: "solo", sisterhood_partner_name: "" })}
                title="Solo"
                price={`$${offering.price_usd.toLocaleString()}`}
                sub="Per person · private space"
                testid="tier-solo"
              />
              {offering.sisterhood_price_usd && (
                <TierCard
                  selected={form.pricing_tier === "sisterhood"}
                  onClick={() => update({ pricing_tier: "sisterhood" })}
                  title={offering.trio_price_usd ? "Sisterhood Duo" : "Sisterhood"}
                  price={`$${offering.sisterhood_price_usd.toLocaleString()}`}
                  sub={`Per person · with ${offering.trio_price_usd ? "1 friend" : "a sister"} (save $${(offering.price_usd - offering.sisterhood_price_usd).toLocaleString()} each)`}
                  testid="tier-sisterhood"
                />
              )}
              {offering.trio_price_usd && (
                <TierCard
                  selected={form.pricing_tier === "trio"}
                  onClick={() => update({ pricing_tier: "trio" })}
                  title="Sisterhood Trio"
                  price={`$${offering.trio_price_usd.toLocaleString()}`}
                  sub={`Per person · with 2 friends (save $${(offering.price_usd - offering.trio_price_usd).toLocaleString()} each)`}
                  testid="tier-trio"
                />
              )}
            </div>
            {(form.pricing_tier === "sisterhood" || form.pricing_tier === "trio") && (
              <div className="mt-5" data-field="sisterhood_partner_name">
                <FormField
                  label={form.pricing_tier === "trio" ? "Who are your 2 sisters? *" : "Who is your sister? *"}
                  name="sisterhood_partner_name"
                  value={form.sisterhood_partner_name}
                  onChange={(v) => update({ sisterhood_partner_name: v })}
                  placeholder={form.pricing_tier === "trio"
                    ? "Their full names — both must register at the trio rate"
                    : "Her full name — she must register too at the sisterhood rate"}
                  testid="sisterhood-partner-input"
                />
              </div>
            )}
          </div>
        )}

        <form onSubmit={submit} className="mt-10 space-y-12" data-testid="retreat-registration-form">
          <Section icon={User} title="Attendee Information" subtitle="So we know who's joining and how to reach you.">
            <Grid>
              <FormField label="Full name *" name="full_name" value={form.full_name} onChange={(v) => update({ full_name: v })} />
              <FormField label="Email *" name="email" type="email" value={form.email} onChange={(v) => update({ email: v })} />
              <FormField label="Phone *" name="phone" value={form.phone} onChange={(v) => update({ phone: v })} />
              <FormField label="Date of birth *" name="dob" type="date" value={form.dob} onChange={(v) => update({ dob: v })} />
            </Grid>
            <FormField label="Mailing address (street · city · state · zip) *" name="address" value={form.address} onChange={(v) => update({ address: v })} multiline rows={2} />
            <Grid>
              <FormField label="Emergency contact name *" name="emergency_contact_name" value={form.emergency_contact_name} onChange={(v) => update({ emergency_contact_name: v })} />
              <FormField label="Relationship *" name="emergency_contact_relationship" value={form.emergency_contact_relationship} onChange={(v) => update({ emergency_contact_relationship: v })} />
              <FormField label="Emergency contact phone *" name="emergency_contact_phone" value={form.emergency_contact_phone} onChange={(v) => update({ emergency_contact_phone: v })} />
            </Grid>
          </Section>

          <Section icon={Heart} title="Your Health & Diet" subtitle="So Holly can keep you safe, nourished, and cared for.">
            <div data-field="health_condition">
              <p className="overline-plum mb-3">Condition of health *</p>
              <Pills options={HEALTH_OPTIONS} value={form.health_condition} onChange={(v) => update({ health_condition: v })} testidPrefix="health" />
            </div>
            <FormField label="Existing health conditions (optional)" value={form.health_notes} onChange={(v) => update({ health_notes: v })} multiline rows={3} />
            <FormField label="Injuries or surgeries in the last 6 months (optional)" value={form.injuries_surgeries} onChange={(v) => update({ injuries_surgeries: v })} multiline rows={3} />
            <FormField label="Current medications (optional)" value={form.medications} onChange={(v) => update({ medications: v })} multiline rows={2} />
            <FormField label="Dietary restrictions / allergies" value={form.dietary_restrictions} onChange={(v) => update({ dietary_restrictions: v })} multiline rows={2} placeholder="e.g. vegetarian, gluten-free, nut allergy" />
          </Section>

          <Section icon={Mountain} title="The Horses & Your Hat" subtitle="We'll fit your cowgirl hat and pair you with a horse that meets you where you are." hidden={offering?.registration_form === "day_retreat" || isRooms}>
            <div data-field="horseback_experience">
              <p className="overline-plum mb-3">Horseback riding experience *</p>
              <Pills options={RIDING_LEVELS} value={form.horseback_experience} onChange={(v) => update({ horseback_experience: v })} testidPrefix="riding" />
            </div>
            <FormField label="Any context that helps Holly match you to the right horse (optional)" value={form.horseback_experience_notes} onChange={(v) => update({ horseback_experience_notes: v })} multiline rows={3} placeholder="When you last rode, what you enjoyed/disliked, anxieties around horses, etc." />

            <div data-field="hat_size">
              <p className="overline-plum mb-3">Cowgirl hat size *</p>
              <p className="text-aviana-muted text-[13px] leading-[1.65] mb-3">
                Wrap a soft tape measure around your head ~1cm above your ears and across the middle of your forehead — that's your circumference in inches. If you're between sizes, round up.
              </p>
              <div className="grid sm:grid-cols-2 gap-2">
                {HAT_SIZES.map((opt) => (
                  <button
                    type="button"
                    key={opt}
                    onClick={() => update({ hat_size: opt })}
                    className={`text-left px-4 py-3 rounded-xl text-[13px] border transition-colors ${
                      form.hat_size === opt
                        ? "bg-aviana-ink text-aviana-cream border-aviana-ink"
                        : "border-aviana-border text-aviana-inkSoft hover:border-aviana-plum"
                    }`}
                    data-testid={`hat-${opt.split(" ")[0].toLowerCase()}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </Section>

          <Section icon={Mountain} title="Time with the Horses" subtitle="We'll spend quiet time grooming and connecting with the horses — no riding required." hidden={!isRooms}>
            <div data-field="horseback_experience">
              <p className="overline-plum mb-3">Your experience around horses</p>
              <Pills options={RIDING_LEVELS} value={form.horseback_experience} onChange={(v) => update({ horseback_experience: v })} testidPrefix="horse-exp" />
            </div>
            <FormField label="Anything Holly should know (optional)" value={form.horseback_experience_notes} onChange={(v) => update({ horseback_experience_notes: v })} multiline rows={2} placeholder="Nervous around horses, allergies, etc." />
          </Section>

          <Section icon={ClipboardList} title="Why You're Here" subtitle="Holly reads every word — this helps her hold space for you.">
            <FormField label="What brought you to this retreat? What are you hoping to release or receive?" value={form.why_this_retreat} onChange={(v) => update({ why_this_retreat: v })} multiline rows={5} />
          </Section>

          <Section icon={ShieldAlert} title="Waivers & Agreements" subtitle="Required for every Aviana registration — including reiki, yoga, aerial yoga, and retreat programming.">
            <Waivers
              liabilityAgreed={form.liability_waiver_agreed}
              onLiabilityChange={(v) => update({ liability_waiver_agreed: v })}
              photoAgreed={form.photo_release_agreed}
              onPhotoChange={(v) => update({ photo_release_agreed: v })}
              testidPrefix="retreat-waiver"
            />

            <div data-field="cancellation_policy_agreed" className="bg-aviana-cream/70 border border-aviana-border rounded-2xl p-6">
              <p className="overline-plum mb-3 flex items-center gap-2"><ShieldAlert size={14} /> Cancellation & refund policy</p>
              <p className="text-aviana-inkSoft text-[14px] leading-[1.75] mb-5">{policy}</p>
              <YesNo
                field="cancellation_policy_inline"
                label="I've read and agree to the cancellation policy. *"
                value={form.cancellation_policy_agreed}
                onChange={(v) => update({ cancellation_policy_agreed: v })}
                compact
              />
            </div>
          </Section>

          {/* Payment chooser */}
          {offering.deposit_usd && (!isRooms || perPersonPrice > 0) && (
            <Section
              icon={CheckCircle2}
              title="How much would you like to pay today?"
              subtitle={`Per-person tuition: $${perPersonPrice.toLocaleString()}. Balance is due ${balanceTiming}.`}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <PaymentChoice
                  selected={form.payment_type === "deposit"}
                  onClick={() => update({ payment_type: "deposit" })}
                  amount={`$${offering.deposit_usd}`}
                  label="Deposit today"
                  sub={`Balance of $${balanceLeft.toLocaleString()} due ${balanceTiming}`}
                  testid="choose-deposit"
                />
                <PaymentChoice
                  selected={form.payment_type === "full"}
                  onClick={() => update({ payment_type: "full" })}
                  amount={`$${perPersonPrice.toLocaleString()}`}
                  label="Pay in full"
                  sub="Done — no balance to worry about"
                  testid="choose-full"
                />
              </div>
              {isRooms && <PaymentPlanPicker offering={offering} form={form} update={update} balance={balanceLeft} />}
            </Section>
          )}

          <div className="pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full !py-4 !text-[13px] tracking-[0.22em] uppercase flex items-center justify-center gap-3"
              data-testid="submit-retreat-registration"
            >
              {submitting ? "Saving your registration…" : <>Continue to payment <ArrowRight size={16} /></>}
            </button>
            <p className="text-center text-[11px] text-aviana-muted mt-4">Next you'll choose how to pay: card (Stripe), Venmo, or Zelle.</p>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, subtitle, children, hidden = false }) {
  if (hidden) return null;
  return (
    <section className="bg-white border border-aviana-border rounded-[1.75rem] p-7 md:p-9">
      <header className="flex items-start gap-4 mb-6">
        <div className="w-10 h-10 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
          <Icon size={18} />
        </div>
        <div>
          <h2 className="font-display text-2xl md:text-3xl text-aviana-ink leading-tight">{title}</h2>
          {subtitle && <p className="mt-1 text-aviana-muted text-[14px] leading-[1.6]">{subtitle}</p>}
        </div>
      </header>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid sm:grid-cols-2 gap-5">{children}</div>;
}

function FormField({ label, name, value, onChange, type = "text", multiline = false, rows = 3, placeholder, testid }) {
  return (
    <label className="block" data-field={name}>
      <span className="overline-plum mb-2 block">{label}</span>
      {multiline ? (
        <textarea
          rows={rows}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full bg-aviana-cream border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink leading-[1.55] focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30"
        />
      ) : (
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full bg-aviana-cream border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30"
        />
      )}
    </label>
  );
}

function YesNo({ field, label, hint, value, onChange, compact }) {
  return (
    <div data-field={field}>
      <p className={`text-aviana-ink font-medium ${compact ? "text-[14px]" : "text-[15px]"} leading-snug`}>{label}</p>
      {hint && !compact && <p className="text-aviana-muted text-[13px] leading-[1.65] mt-1.5">{hint}</p>}
      <div className="mt-3 flex gap-2">
        {[
          { v: true, l: "Yes" },
          { v: false, l: "No" },
        ].map((opt) => (
          <button
            key={String(opt.v)}
            type="button"
            onClick={() => onChange(opt.v)}
            className={`px-6 py-2 rounded-full text-[12px] tracking-[0.18em] uppercase border transition-colors ${
              value === opt.v
                ? "bg-aviana-ink text-aviana-cream border-aviana-ink"
                : "border-aviana-border text-aviana-inkSoft hover:border-aviana-plum"
            }`}
            data-testid={`${field}-${opt.l.toLowerCase()}`}
          >
            {opt.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function PaymentChoice({ selected, onClick, amount, label, sub, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-5 rounded-2xl border transition-all ${
        selected ? "border-aviana-ink bg-aviana-cream" : "border-aviana-border hover:border-aviana-plum/40"
      }`}
    >
      <div className="font-display text-3xl text-aviana-ink">{amount}</div>
      <div className="text-[12px] tracking-[0.18em] uppercase text-aviana-plum mt-1">{label}</div>
      <div className="text-[13px] text-aviana-muted leading-[1.55] mt-2">{sub}</div>
    </button>
  );
}

function Pills({ options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`px-5 py-2.5 rounded-full text-[12px] tracking-[0.15em] uppercase border transition-colors ${
            value === opt
              ? "bg-aviana-ink text-aviana-cream border-aviana-ink"
              : "border-aviana-border text-aviana-inkSoft hover:border-aviana-plum"
          }`}
          data-testid={`${testidPrefix}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function TierCard({ selected, onClick, title, price, sub, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-5 rounded-2xl border transition-all ${
        selected ? "border-aviana-ink bg-aviana-cream" : "border-aviana-border hover:border-aviana-plum/40"
      }`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="overline-plum">{title}</p>
        {selected && <Sparkles size={14} className="text-aviana-gold" />}
      </div>
      <div className="font-display text-3xl text-aviana-ink mt-2">{price}</div>
      <div className="text-[12px] text-aviana-muted leading-[1.55] mt-2">{sub}</div>
    </button>
  );
}
